// src/features/october/components/ProductCard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShoppingBag } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { useWishlist } from '../hooks/useCart';

const ProductCard = ({ product, index = 0, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { addToCart, isInCart, getItemQuantity } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  // Refined animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: index * 0.05,
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Enhanced add to cart functionality
  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!product.inStock || isAddingToCart) return;
    
    setIsAddingToCart(true);
    
    try {
      addToCart(product, 1);
      setTimeout(() => setIsAddingToCart(false), 1200);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setIsAddingToCart(false);
    }
  };

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const inCart = isInCart(product.id);
  const inWishlist = isInWishlist(product.id);
  const cartQuantity = getItemQuantity(product.id);

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      className={`group relative bg-brand-black/80 overflow-hidden rounded-2xl transition-all duration-500 border border-brand-red/20 hover:border-brand-gold/40 hover:-translate-y-1 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Product Link */}
      <Link to={`/kganya-scents/product/${product.id}`} className="block">
        
        {/* Image Container - Portrait luxury */}
        <div className="relative overflow-hidden bg-brand-black aspect-[3/4] rounded-t-2xl">
          
          {/* Clean loading placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-brand-black/70 animate-pulse" />
          )}
          
          {/* Product Image - Portrait, fills container */}
          <motion.img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-all duration-700 group-hover:scale-105"
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          />

          {/* Minimal Status Badges - Top Left */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {product.featured && (
              <span className="bg-brand-gold text-brand-black px-3 py-1 rounded-lg text-xs font-body font-medium tracking-[0.2em] uppercase">
                FEATURED
              </span>
            )}
            {!product.inStock && (
              <span className="bg-brand-black/80 text-brand-light/50 px-3 py-1 rounded-lg text-xs font-body font-medium tracking-[0.2em] uppercase border border-brand-light/10">
                SOLD OUT
              </span>
            )}
          </div>

          {/* Minimal Action Buttons - Top Right */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-4 right-4 flex flex-col gap-2"
              >
                {/* Wishlist Button */}
                <motion.button
                  onClick={handleWishlistToggle}
                  className={`p-3 rounded-lg backdrop-blur-sm transition-all duration-200 border ${
                    inWishlist 
                      ? 'bg-brand-red/15 text-brand-gold border-brand-gold/40' 
                      : 'bg-brand-black/80 text-brand-light/80 hover:text-brand-gold hover:border-brand-gold/40 border-brand-red/20'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Heart size={16} fill={inWishlist ? "currentColor" : "none"} strokeWidth={1.5} />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Minimal Cart Action - Bottom */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-4 left-4 right-4"
              >
                {inCart ? (
                  <div className="flex items-center justify-center gap-2 bg-brand-red/20 backdrop-blur-sm border border-brand-red/40 text-brand-gold px-4 py-3 rounded-lg font-body font-medium tracking-wide">
                    <div className="w-2 h-2 bg-brand-red rounded-full" />
                    <span className="text-sm">IN CART ({cartQuantity})</span>
                  </div>
                ) : (
                  <button
                    onClick={handleQuickAdd}
                    disabled={!product.inStock || isAddingToCart}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-body font-medium tracking-wide transition-all duration-200 backdrop-blur-sm border ${
                      product.inStock && !isAddingToCart
                        ? 'bg-brand-red text-brand-light hover:bg-brand-red/80 border-brand-red/40'
                        : isAddingToCart
                        ? 'bg-brand-black/70 text-brand-light/60 border-brand-light/10'
                        : 'bg-brand-black/70 text-brand-light/40 cursor-not-allowed border-brand-light/10'
                    }`}
                  >
                    {isAddingToCart ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 border-2 border-brand-light/60 border-t-brand-red rounded-full"
                        />
                        <span className="text-sm tracking-[0.2em]">ADDING</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={16} strokeWidth={1.5} />
                        <span className="text-sm tracking-[0.2em]">{product.inStock ? 'ADD TO CART' : 'SOLD OUT'}</span>
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Ultra Minimal Product Info */}
        <div className="p-6 text-center bg-brand-black border-t border-brand-red/20">
          <h3 className="font-heading font-medium text-brand-gold text-base leading-tight mb-2 tracking-wide">
            {product.name}
          </h3>
          <p className="text-sm font-body font-medium text-brand-light tracking-[0.25em] uppercase">
            {product.price}
          </p>
        </div>
      </Link>

      {/* Mobile Quick Action */}
      <div className="sm:hidden absolute top-3 right-3 z-10">
        <AnimatePresence>
          {inCart ? (
            <div className="bg-brand-black/80 text-brand-gold rounded-lg p-2 border border-brand-gold/30 backdrop-blur-sm">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-brand-red rounded-full" />
                <span className="text-xs font-body font-medium">{cartQuantity}</span>
              </div>
            </div>
          ) : (
            <motion.button
              onClick={handleQuickAdd}
              disabled={!product.inStock || isAddingToCart}
              className={`rounded-lg p-2.5 transition-all duration-200 border backdrop-blur-sm ${
                product.inStock && !isAddingToCart
                  ? 'bg-brand-black/80 text-brand-gold hover:text-brand-light hover:border-brand-gold/40 border-brand-red/20'
                  : isAddingToCart
                  ? 'bg-brand-black/70 text-brand-light/60 border-brand-light/10'
                  : 'bg-brand-black/70 text-brand-light/40 cursor-not-allowed border-brand-light/10'
              }`}
              whileHover={{ scale: product.inStock && !isAddingToCart ? 1.05 : 1 }}
              whileTap={{ scale: product.inStock && !isAddingToCart ? 0.95 : 1 }}
            >
              {isAddingToCart ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-brand-light/60 border-t-brand-red rounded-full"
                />
              ) : (
                <ShoppingBag size={16} strokeWidth={1.5} />
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ProductCard;
